import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '@api-client/types';

export interface ProviderInterface {
  id: number;
  uuid: string;
  name: string;
  nominal_code: string;
  claims_email: string;
  claims_telephone: string;
  support_email: string;
  support_telephone: string;
  complaints_email: string;
  logo: {
    id: number;
    file_type: string;
    file_name: string;
    file_path: string;
  } | null;
}

export interface ProvidersInterface {
  provider: ProviderInterface;
  provider_function: {
    id: number;
    name: string;
  };
  provider_role: {
    id: number;
    name: string;
  };
  star_rating: number;
  commission: number;
  consumer_duties: string;
}

export class ApiProviders extends BaseApiClient {
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<ProviderInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/providers', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async store(payload: any): Promise<ProviderInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/providers', payload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async update(uuid: string, payload: any): Promise<ProviderInterface> {
    const formData = new FormData();

    for (const [key, value] of Object.entries(payload)) {
      formData.set(key, value as string | Blob);
    }

    formData.append('_method', 'PUT');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/providers/${uuid}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteProvider(uuid: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/providers/${uuid}`)
        .then((res) => {
          if (res.status === 204) {
            resolve();
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getFunctions(): Promise<
    ListDataResponse<{
      id: number;
      name: string;
    }>
  > {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/providers/functions')
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getRoles(): Promise<
    ListDataResponse<{
      id: number;
      name: string;
    }>
  > {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/providers/roles')
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { providersQueryKeys } from './query-keys';
export type { ProvidersListQueryParams } from './query-keys';
export {
  useProvidersFunctionsQuery,
  useProvidersListQuery,
  useProvidersRolesQuery
} from './use-providers-queries';
export {
  useProvidersDeleteMutation,
  useProvidersStoreMutation,
  useProvidersUpdateMutation
} from './use-providers-mutations';
export type { ProvidersUpdateVariables } from './use-providers-mutations';
